// Pre-configured report templates for all 11 default reports
// Based on reports.md with actual field mappings
// Updated: October 14, 2025 - WORKING TEMPLATES

export interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  selectedFields: string[];
  calculatedFields: any[];
  conditions: any[];
  groupBy?: any;
  orderBy?: any[];
  chartConfig?: any;
  isPublic: boolean;
  exportFormats: string[];
}

export const REPORT_TEMPLATES: ReportTemplate[] = [
  // ========================================
  // Report 1: System Dashboard
  // ========================================
  {
    id: 'system_dashboard',
    name: '1. System Dashboard',
    description: 'High-level system metrics: active subscriptions, users, reservations, and month-over-month development',
    category: 'Dashboard',
    selectedFields: [
      'subscriber_id',
      'subscriber_status',
      'user_id',
      'reservation_id',
      'reservation_created_date'
    ],
    calculatedFields: [],
    conditions: [],
    groupBy: {
      fields: [],
      aggregations: [
        {
          field: 'subscriber_id',
          function: 'COUNT_DISTINCT',
          alias: 'active_subscriptions',
          filter: {
            field: 'subscriber_status',
            operator: 'equals',
            value: 'Active'
          }
        },
        {
          field: 'user_id',
          function: 'COUNT_DISTINCT',
          alias: 'total_users'
        },
        {
          field: 'reservation_id',
          function: 'COUNT',
          alias: 'total_reservations'
        },
        {
          field: 'reservation_id',
          function: 'COUNT',
          alias: 'current_month_reservations',
          filter: {
            field: 'reservation_created_date',
            operator: 'greater_than_equal',
            value: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString()
          }
        },
        {
          field: 'reservation_id',
          function: 'COUNT',
          alias: 'previous_month_reservations',
          filter: {
            field: 'reservation_created_date',
            operator: 'between',
            value: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toISOString(),
            secondValue: new Date(new Date().getFullYear(), new Date().getMonth(), 0).toISOString()
          }
        }
      ]
    },
    orderBy: [],
    chartConfig: {
      enabled: true,
      type: 'bar',
      title: 'System Dashboard Metrics',
      xAxis: 'metric',
      yAxis: ['active_subscriptions', 'total_users', 'total_reservations']
    },
    isPublic: true,
    exportFormats: ['csv', 'xls', 'pdf']
  },

  // ========================================
  // Report 2: Contract Alert (SIMPLIFIED - ignore subscription expiration)
  // ========================================
  {
    id: 'contract_alert',
    name: '2. Contract Alert',
    description: 'Businesses with their user count and recent reservation activity',
    category: 'Alerts',
    selectedFields: [
      'customer_business_name',
      'user_id',
      'reservation_id',
      'reservation_created_date'
    ],
    calculatedFields: [],
    conditions: [
      {
        field: 'reservation_created_date',
        operator: 'greater_than_equal',
        value: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString() // Past 30 days
      }
    ],
    groupBy: {
      fields: ['customer_business_name'],
      aggregations: [
        {
          field: 'user_id',
          function: 'COUNT_DISTINCT',
          alias: 'number_of_users'
        },
        {
          field: 'reservation_id',
          function: 'COUNT',
          alias: 'reservations_past_30_days'
        }
      ]
    },
    orderBy: [
      {
        field: 'reservations_past_30_days',
        direction: 'desc'
      }
    ],
    chartConfig: null,
    isPublic: false,
    exportFormats: ['csv', 'xls']
  },

  // ========================================
  // Report 3: Leaderboard
  // ========================================
  {
    id: 'leaderboard',
    name: '3. Leaderboard',
    description: 'FSR performance ranking by contracts, value, and conversion rate',
    category: 'Performance',
    selectedFields: [
      'user_first_name',
      'user_last_name',
      'contract_id',
      'contract_value',
      'contract_created_date',
      'reservation_id',
      'reservation_status'
    ],
    calculatedFields: [],
    conditions: [],
    groupBy: {
      fields: ['user_first_name', 'user_last_name'],
      aggregations: [
        {
          field: 'contract_id',
          function: 'COUNT',
          alias: 'contracts_past_month',
          filter: {
            field: 'contract_created_date',
            operator: 'greater_than_equal',
            value: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
          }
        },
        {
          field: 'contract_value',
          function: 'SUM',
          alias: 'contract_value_past_month',
          filter: {
            field: 'contract_created_date',
            operator: 'greater_than_equal',
            value: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
          }
        },
        {
          field: 'reservation_id',
          function: 'COUNT',
          alias: 'active_reservations',
          filter: {
            field: 'reservation_status',
            operator: 'in',
            value: ['Pending', 'Interested']
          }
        },
        {
          field: 'reservation_id',
          function: 'COUNT',
          alias: 'total_reservations'
        },
        {
          field: 'contract_id',
          function: 'COUNT',
          alias: 'total_contracts'
        }
      ]
    },
    orderBy: [
      {
        field: 'contract_value_past_month',
        direction: 'desc'
      }
    ],
    chartConfig: {
      enabled: true,
      type: 'bar',
      title: 'FSR Leaderboard - Contract Value',
      xAxis: 'user_first_name',
      yAxis: ['contract_value_past_month']
    },
    isPublic: true,
    exportFormats: ['csv', 'xls', 'pdf']
  },

  // ========================================
  // Report 4: Product Report (12 Month Rolling)
  // ========================================
  {
    id: 'product_report',
    name: '4. Product Report (Rolling 12 Months)',
    description: 'Product performance over the last 12 months - contract count and value',
    category: 'Sales',
    selectedFields: [
      'product_name',
      'contract_id',
      'contract_value',
      'contract_created_date'
    ],
    calculatedFields: [],
    conditions: [
      {
        field: 'contract_created_date',
        operator: 'greater_than_equal',
        value: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString()
      }
    ],
    groupBy: {
      fields: ['product_name'],
      aggregations: [
        {
          field: 'contract_id',
          function: 'COUNT',
          alias: 'total_contracts'
        },
        {
          field: 'contract_value',
          function: 'SUM',
          alias: 'total_value'
        }
      ]
    },
    orderBy: [
      {
        field: 'total_value',
        direction: 'desc'
      }
    ],
    chartConfig: {
      enabled: true,
      type: 'line',
      title: 'Product Performance - Rolling 12 Months',
      xAxis: 'product_name',
      yAxis: ['total_contracts', 'total_value']
    },
    isPublic: false,
    exportFormats: ['csv', 'xls', 'pdf']
  },

  // ========================================
  // Report 5: Team Activity Report (Daily Interactions)
  // ========================================
  {
    id: 'team_activity',
    name: '5. Team Activity Report',
    description: 'Daily interaction activity for the past 7 days per user',
    category: 'Activity',
    selectedFields: [
      'user_first_name',
      'user_last_name',
      'interaction_id',
      'interaction_date'
    ],
    calculatedFields: [],
    conditions: [
      {
        field: 'interaction_date',
        operator: 'greater_than_equal',
        value: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString()
      }
    ],
    groupBy: {
      fields: ['user_first_name', 'user_last_name'],
      aggregations: [
        {
          field: 'interaction_id',
          function: 'COUNT',
          alias: 'total_past_7_days'
        }
      ]
    },
    orderBy: [
      {
        field: 'total_past_7_days',
        direction: 'desc'
      }
    ],
    chartConfig: {
      enabled: true,
      type: 'bar',
      title: 'Team Activity - Past 7 Days',
      xAxis: 'user_first_name',
      yAxis: ['total_past_7_days']
    },
    isPublic: false,
    exportFormats: ['csv', 'xls']
  },

  // ========================================
  // Report 6: Pipeline Alert
  // ========================================
  {
    id: 'pipeline_alert',
    name: '6. Pipeline Alert',
    description: 'Active reservations and those expiring in the next 7 days',
    category: 'Alerts',
    selectedFields: [
      'user_first_name',
      'user_last_name',
      'reservation_id',
      'reservation_status',
      'reservation_expiry_date'
    ],
    calculatedFields: [],
    conditions: [],
    groupBy: {
      fields: ['user_first_name', 'user_last_name'],
      aggregations: [
        {
          field: 'reservation_id',
          function: 'COUNT',
          alias: 'active_reservations',
          filter: {
            field: 'reservation_status',
            operator: 'in',
            value: ['Pending', 'Interested']
          }
        },
        {
          field: 'reservation_id',
          function: 'COUNT',
          alias: 'expiring_next_7_days',
          filter: {
            field: 'reservation_expiry_date',
            operator: 'between',
            value: new Date().toISOString(),
            secondValue: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
          }
        }
      ]
    },
    orderBy: [
      {
        field: 'expiring_next_7_days',
        direction: 'desc'
      }
    ],
    chartConfig: null,
    isPublic: false,
    exportFormats: ['csv', 'xls', 'pdf']
  },

  // ========================================
  // Report 7: FSR Team Performance Review
  // ========================================
  {
    id: 'fsr_performance',
    name: '7. FSR Team Performance Review',
    description: 'Comprehensive FSR performance: wins, losses, value, and top products',
    category: 'Performance',
    selectedFields: [
      'user_first_name',
      'user_last_name',
      'contract_id',
      'contract_value',
      'reservation_id',
      'reservation_status',
      'interaction_id',
      'interaction_date',
      'product_name'
    ],
    calculatedFields: [],
    conditions: [],
    groupBy: {
      fields: ['user_first_name', 'user_last_name'],
      aggregations: [
        {
          field: 'contract_id',
          function: 'COUNT',
          alias: 'wins'
        },
        {
          field: 'reservation_id',
          function: 'COUNT',
          alias: 'losses',
          filter: {
            field: 'reservation_status',
            operator: 'in',
            value: ['Expired', 'Not Interested']
          }
        },
        {
          field: 'contract_value',
          function: 'SUM',
          alias: 'total_value_of_wins'
        },
        {
          field: 'interaction_id',
          function: 'COUNT',
          alias: 'total_interactions',
          filter: {
            field: 'interaction_date',
            operator: 'greater_than_equal',
            value: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString()
          }
        }
      ]
    },
    orderBy: [
      {
        field: 'total_value_of_wins',
        direction: 'desc'
      }
    ],
    chartConfig: {
      enabled: true,
      type: 'line',
      title: 'FSR Performance - 1 Year Trend',
      xAxis: 'user_first_name',
      yAxis: ['wins', 'total_value_of_wins']
    },
    isPublic: true,
    exportFormats: ['csv', 'xls', 'pdf']
  },

  // ========================================
  // Report 8: Personal Pipeline Report
  // ========================================
  {
    id: 'personal_pipeline',
    name: '8. Personal Pipeline Report',
    description: 'Individual user\'s active reservations with customer and product details',
    category: 'Personal',
    selectedFields: [
      'reservation_id',
      'reservation_ref_no',
      'customer_business_name',
      'reservation_product_ids',
      'reservation_status',
      'reservation_expiry_date'
    ],
    calculatedFields: [],
    conditions: [
      {
        field: 'reservation_status',
        operator: 'in',
        value: ['Pending', 'Interested']
      }
    ],
    groupBy: null,
    orderBy: [
      {
        field: 'reservation_expiry_date',
        direction: 'asc'
      }
    ],
    chartConfig: null,
    isPublic: false,
    exportFormats: ['csv', 'xls']
  },

  // ========================================
  // Report 9: Activity Report
  // ========================================
  {
    id: 'activity_report',
    name: '9. Activity Report',
    description: 'Customer interactions log with type and timestamp',
    category: 'Activity',
    selectedFields: [
      'customer_business_name',
      'interaction_type',
      'interaction_date',
      'interaction_time'
    ],
    calculatedFields: [],
    conditions: [],
    groupBy: null,
    orderBy: [
      {
        field: 'interaction_date',
        direction: 'desc'
      }
    ],
    chartConfig: null,
    isPublic: false,
    exportFormats: ['csv', 'xls']
  },

  // ========================================
  // Report 10: Monthly Win/Loss Report (Combined)
  // ========================================
  {
    id: 'win_loss_report',
    name: '10. Monthly Win/Loss Report',
    description: 'Combined overview of won contracts and lost reservations',
    category: 'Sales',
    selectedFields: [
      'reservation_id',
      'reservation_ref_no',
      'customer_business_name',
      'reservation_product_ids',
      'reservation_status',
      'contract_id',
      'contract_status',
      'contract_value',
      'contract_created_date',
      'reservation_expired_on'
    ],
    calculatedFields: [],
    conditions: [],
    groupBy: null,
    orderBy: [
      {
        field: 'contract_created_date',
        direction: 'desc'
      }
    ],
    chartConfig: {
      enabled: true,
      type: 'pie',
      title: 'Win/Loss Distribution',
      xAxis: 'reservation_status',
      yAxis: ['contract_value']
    },
    isPublic: false,
    exportFormats: ['csv', 'xls', 'pdf']
  },

  // ========================================
  // Report 11: Personal Performance Review
  // ========================================
  {
    id: 'personal_performance',
    name: '11. Personal Performance Review',
    description: 'Quarterly performance metrics for individual users',
    category: 'Personal',
    selectedFields: [
      'user_first_name',
      'user_last_name',
      'contract_id',
      'contract_value',
      'contract_created_date',
      'reservation_id',
      'reservation_status',
      'interaction_id',
      'interaction_date'
    ],
    calculatedFields: [],
    conditions: [
      {
        field: 'contract_created_date',
        operator: 'between',
        // Last completed quarter - adjust based on current quarter
        value: new Date(new Date().getFullYear(), Math.floor((new Date().getMonth() - 3) / 3) * 3, 1).toISOString(),
        secondValue: new Date(new Date().getFullYear(), Math.floor((new Date().getMonth() - 3) / 3) * 3 + 3, 0).toISOString()
      }
    ],
    groupBy: {
      fields: ['user_first_name', 'user_last_name'],
      aggregations: [
        {
          field: 'contract_id',
          function: 'COUNT',
          alias: 'wins'
        },
        {
          field: 'reservation_id',
          function: 'COUNT',
          alias: 'losses',
          filter: {
            field: 'reservation_status',
            operator: 'in',
            value: ['Expired', 'Not Interested']
          }
        },
        {
          field: 'interaction_id',
          function: 'COUNT',
          alias: 'interactions_per_week'
        },
        {
          field: 'contract_id',
          function: 'COUNT',
          alias: 'new_contracts'
        },
        {
          field: 'contract_value',
          function: 'SUM',
          alias: 'new_contract_value'
        }
      ]
    },
    orderBy: [],
    chartConfig: {
      enabled: true,
      type: 'bar',
      title: 'Personal Performance - Last Quarter',
      xAxis: 'user_first_name',
      yAxis: ['new_contracts', 'new_contract_value']
    },
    isPublic: false,
    exportFormats: ['csv', 'xls', 'pdf']
  },

  // ========================================
  // DEMO TEMPLATE: Complete System Analytics Dashboard
  // ========================================
  {
    id: 'comprehensive_demo_dashboard',
    name: '🚀 Complete System Analytics Dashboard - Demo Template',
    description: 'COMPREHENSIVE DEMO: Showcases every feature of the custom-reports system. Demonstrates field selection, calculated fields, advanced filtering, grouping, aggregations, sorting, and chart visualization with real business data. Perfect for training, demos, and showcasing system capabilities.',
    category: 'Analytics',
    selectedFields: [
      // USER INFORMATION (3 fields)
      'user_first_name',
      'user_last_name',
      'user_email',
      
      // SUBSCRIBER INFORMATION (2 fields)
      'subscriber_company_name',
      'subscriber_status',
      
      // CUSTOMER INFORMATION (4 fields)
      'customer_business_name',
      'customer_industry',
      'customer_interest_level',
      'customer_created_date',
      
      // PRODUCT INFORMATION (3 fields)
      'product_name',
      'product_min_price',
      'product_max_price',
      
      // RESERVATION INFORMATION (4 fields)
      'reservation_id',
      'reservation_status',
      'reservation_created_date',
      'reservation_expiry_date',
      
      // CONTRACT INFORMATION (3 fields)
      'contract_id',
      'contract_value',
      'contract_status',
      
      // INTERACTION INFORMATION (2 fields)
      'interaction_id',
      'interaction_type'
    ],
    calculatedFields: [
      // MATHEMATICAL CALCULATIONS (2 fields)
      {
        name: 'product_price_range',
        label: 'Product Price Range',
        formula: 'product_max_price - product_min_price',
        type: 'mathematical',
        returnType: 'number',
        description: 'Price flexibility range for each product - shows pricing strategy flexibility'
      },
      {
        name: 'average_product_price',
        label: 'Average Product Price',
        formula: '(product_min_price + product_max_price) / 2',
        type: 'mathematical',
        returnType: 'number',
        description: 'Midpoint price for competitive analysis and pricing strategy'
      },
      
      // DATE OPERATIONS (2 fields)
      {
        name: 'reservation_age_days',
        label: 'Reservation Age (Days)',
        formula: 'DATEDIFF(CURRENT_DATE(), reservation_created_date)',
        type: 'date_operation',
        returnType: 'number',
        description: 'How long reservation has been active - identifies aging pipeline'
      },
      {
        name: 'days_until_expiry',
        label: 'Days Until Expiry',
        formula: 'DATEDIFF(reservation_expiry_date, CURRENT_DATE())',
        type: 'date_operation',
        returnType: 'number',
        description: 'Urgency metric for reservation follow-up - prioritizes actions'
      }
    ],
    conditions: [
      // DATE RANGE FILTERS - Shows filtering capabilities
      {
        field: 'reservation_created_date',
        operator: 'greater_than_equal',
        value: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(), // Last 12 months
        description: 'Focus on recent reservations - demonstrates date range filtering'
      },
      
      // STATUS FILTERS - Shows exact match filtering
      {
        field: 'subscriber_status',
        operator: 'equals',
        value: 'Active',
        description: 'Only active subscribers - demonstrates status filtering'
      },
      
      // VALUE FILTERS - Shows numeric comparison filtering
      {
        field: 'contract_value',
        operator: 'greater_than',
        value: 0,
        description: 'Exclude zero-value contracts - demonstrates value filtering'
      },
      
      // COMPLEX FILTERS - Shows multiple value exclusion filtering
      {
        field: 'reservation_status',
        operator: 'not_in',
        value: ['Expired', 'Cancelled'],
        description: 'Exclude closed reservations - demonstrates complex filtering'
      }
    ],
    groupBy: {
      fields: ['customer_industry'],
      aggregations: [
        // COUNT AGGREGATIONS - Shows counting capabilities
        {
          field: 'customer_business_name',
          function: 'COUNT',
          alias: 'total_customers',
          description: 'Count unique customers per industry'
        },
        {
          field: 'reservation_id',
          function: 'COUNT',
          alias: 'total_reservations',
          description: 'Count all reservations per industry'
        },
        {
          field: 'contract_id',
          function: 'COUNT',
          alias: 'total_contracts',
          description: 'Count contracts per industry'
        },
        
        // SUM AGGREGATION - Shows summation capabilities
        {
          field: 'contract_value',
          function: 'SUM',
          alias: 'total_revenue',
          description: 'Sum contract values per industry'
        },
        
        // AVG AGGREGATION - Shows average calculation capabilities
        {
          field: 'contract_value',
          function: 'AVG',
          alias: 'average_deal_size',
          description: 'Average contract value per industry'
        }
      ],
      having: [
        // POST-AGGREGATION FILTERING - Shows HAVING clause capabilities
        {
          field: 'total_revenue',
          operator: 'greater_than',
          value: 1000,
          description: 'Focus on industries with meaningful revenue - demonstrates HAVING clause'
        }
      ]
    },
    orderBy: [
      // MULTIPLE SORT CRITERIA - Shows advanced sorting
      {
        field: 'total_revenue',
        direction: 'desc',
        description: 'Sort by revenue descending - shows primary sorting'
      },
      {
        field: 'total_customers',
        direction: 'desc',
        description: 'Then by customer count - shows secondary sorting'
      },
      {
        field: 'average_deal_size',
        direction: 'desc',
        description: 'Finally by deal size - shows tertiary sorting'
      }
    ],
    chartConfig: {
      enabled: true,
      type: 'bar',
      title: 'Revenue Analysis by Industry',
      xAxis: 'customer_industry',
      yAxis: ['total_revenue', 'total_customers', 'average_deal_size'],
      description: 'Multi-dimensional analysis showing revenue, customer count, and deal size by industry',
      colors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Comprehensive Business Analytics Dashboard',
            font: { size: 16, weight: 'bold' }
          },
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(context: any) {
                const label = context.dataset.label || '';
                const value = context.parsed.y;
                if (label.includes('Revenue')) {
                  return `${label}: $${value.toLocaleString()}`;
                } else if (label.includes('Deal Size')) {
                  return `${label}: $${value.toLocaleString()}`;
                } else {
                  return `${label}: ${value.toLocaleString()}`;
                }
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { display: true },
            title: {
              display: true,
              text: 'Values'
            },
            ticks: {
              callback: function(value: any) {
                if (value >= 1000) {
                  return '$' + (value / 1000) + 'K';
                }
                return '$' + value;
              }
            }
          },
          x: {
            grid: { display: false },
            title: {
              display: true,
              text: 'Industry'
            }
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        }
      }
    },
    isPublic: true,
    exportFormats: ['csv', 'xls', 'pdf']
  },

  // ========================================
  // NEW COMPREHENSIVE DEMO: Business Intelligence Dashboard
  // ========================================
  {
    id: 'business_intelligence_dashboard',
    name: '📊 Business Intelligence Dashboard - Complete Demo',
    description: 'COMPREHENSIVE BUSINESS DEMO: Demonstrates all 6 steps of the custom-reports system with meaningful business data. Covers field selection (14 fields), calculated fields (2 business calculations), simple filtering (1 condition), individual record display, and chart visualization. Shows reservations, customers, users, subscribers, and contracts with proper data joining.',
    category: 'Analytics',
    selectedFields: [
      // RESERVATION INFORMATION (Primary collection)
      'reservation_id',
      'reservation_status',
      'reservation_created_date',
      
      // CUSTOMER INFORMATION (Joined from customerId)
      'customer_business_name',
      'customer_industry',
      'customer_interest_level',
      
      // USER/FSR INFORMATION (Joined from userId)
      'user_first_name',
      'user_last_name',
      'user_email',
      
      // SUBSCRIBER INFORMATION (Joined from subscriberId)
      'subscriber_company_name',
      'subscriber_status',
      
      // CONTRACT INFORMATION (Joined via customerId)
      'contract_id',
      'contract_value',
      'contract_status'
    ],
    calculatedFields: [
      // BUSINESS CALCULATIONS (2 fields)
      {
        name: 'contract_value_formatted',
        label: 'Contract Value (Formatted)',
        formula: 'contract_value',
        type: 'mathematical',
        returnType: 'number',
        description: 'Formatted contract value for display'
      },
      {
        name: 'reservation_age_days',
        label: 'Reservation Age (Days)',
        formula: 'DATEDIFF(CURRENT_DATE(), reservation_created_date)',
        type: 'date_operation',
        returnType: 'number',
        description: 'How long reservation has been active'
      }
    ],
    conditions: [
      // SIMPLE STATUS FILTER - Only exclude expired reservations
      {
        field: 'reservation_status',
        operator: 'not_equals',
        value: 'Expired',
        description: 'Exclude expired reservations only'
      }
    ],
    groupBy: null, // Show individual records instead of grouping
    orderBy: [
      // SIMPLE SORTING BY CUSTOMER INDUSTRY
      {
        field: 'customer_industry',
        direction: 'asc',
        description: 'Sort by customer industry'
      },
      {
        field: 'customer_business_name',
        direction: 'asc',
        description: 'Then by customer business name'
      }
    ],
    chartConfig: {
      enabled: true,
      type: 'pie',
      title: 'Reservations by Industry Distribution',
      xAxis: 'customer_industry',
      yAxis: ['customer_business_name'],
      description: 'Distribution of reservations across different industries',
      colors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Business Intelligence Dashboard',
            font: { size: 16, weight: 'bold' }
          },
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(context: any) {
                const label = context.dataset.label || '';
                const value = context.parsed.y;
                if (label.includes('Revenue')) {
                  return `${label}: $${value.toLocaleString()}`;
                } else if (label.includes('Deal Size')) {
                  return `${label}: $${value.toLocaleString()}`;
                } else {
                  return `${label}: ${value.toLocaleString()}`;
                }
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { display: true },
            title: {
              display: true,
              text: 'Values'
            },
            ticks: {
              callback: function(value: any) {
                if (value >= 1000) {
                  return '$' + (value / 1000) + 'K';
                }
                return '$' + value;
              }
            }
          },
          x: {
            grid: { display: false },
            title: {
              display: true,
              text: 'Industry'
            }
          }
        }
      }
    },
    isPublic: true,
    exportFormats: ['csv', 'xls', 'pdf']
  },

  // ========================================
  // PRODUCT ANALYSIS TEMPLATE: Product Performance Dashboard
  // ========================================
  {
    id: 'product_analysis_dashboard',
    name: '📦 Product Performance Dashboard - Multi-Product Analysis',
    description: 'PRODUCT FOCUSED DEMO: Demonstrates product analysis capabilities with proper handling of multiple products per reservation/contract. Covers field selection (12 fields), calculated fields (2 product calculations), filtering, grouping by product, and chart visualization. Perfect for analyzing product performance across reservations and contracts.',
    category: 'Analytics',
    selectedFields: [
      // PRODUCT INFORMATION (Primary focus)
      'product_name',
      'product_min_price',
      'product_max_price',
      
      // RESERVATION INFORMATION
      'reservation_id',
      'reservation_status',
      'reservation_created_date',
      
      // CUSTOMER INFORMATION
      'customer_business_name',
      'customer_industry',
      
      // CONTRACT INFORMATION
      'contract_id',
      'contract_value',
      'contract_status',
      
      // USER/FSR INFORMATION
      'user_first_name',
      'user_last_name'
    ],
    calculatedFields: [
      // PRODUCT CALCULATIONS
      {
        name: 'product_price_range',
        label: 'Product Price Range',
        formula: 'product_max_price - product_min_price',
        type: 'mathematical',
        returnType: 'number',
        description: 'Price flexibility range for each product'
      },
      {
        name: 'average_product_price',
        label: 'Average Product Price',
        formula: '(product_min_price + product_max_price) / 2',
        type: 'mathematical',
        returnType: 'number',
        description: 'Midpoint price for competitive analysis'
      }
    ],
    conditions: [
      // SIMPLE STATUS FILTER
      {
        field: 'reservation_status',
        operator: 'not_equals',
        value: 'Expired',
        description: 'Exclude expired reservations'
      }
    ],
    groupBy: {
      fields: ['product_name'],
      aggregations: [
        // COUNT AGGREGATIONS
        {
          field: 'reservation_id',
          function: 'COUNT',
          alias: 'total_reservations',
          description: 'Count reservations per product'
        },
        {
          field: 'contract_id',
          function: 'COUNT',
          alias: 'total_contracts',
          description: 'Count contracts per product'
        },
        
        // SUM AGGREGATION
        {
          field: 'contract_value',
          function: 'SUM',
          alias: 'total_revenue',
          description: 'Sum contract values per product'
        },
        
        // AVG AGGREGATION
        {
          field: 'contract_value',
          function: 'AVG',
          alias: 'average_deal_size',
          description: 'Average contract value per product'
        }
      ]
    },
    orderBy: [
      {
        field: 'total_revenue',
        direction: 'desc',
        description: 'Sort by revenue descending'
      },
      {
        field: 'total_reservations',
        direction: 'desc',
        description: 'Then by reservation count'
      }
    ],
    chartConfig: {
      enabled: true,
      type: 'bar',
      title: 'Product Performance Analysis',
      xAxis: 'product_name',
      yAxis: ['total_revenue', 'total_reservations', 'average_deal_size'],
      description: 'Multi-dimensional analysis showing revenue, reservations, and deal size by product',
      colors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Product Performance Dashboard',
            font: { size: 16, weight: 'bold' }
          },
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            mode: 'index',
            intersect: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { display: true },
            title: {
              display: true,
              text: 'Values'
            }
          },
          x: {
            grid: { display: false },
            title: {
              display: true,
              text: 'Product'
            }
          }
        }
      }
    },
    isPublic: true,
    exportFormats: ['csv', 'xls', 'pdf']
  },

];

export const TEMPLATE_CATEGORIES = {
  'Dashboard': {
    name: 'Dashboard',
    description: 'High-level system overview and metrics',
    icon: 'pi-chart-bar',
    color: '#3B82F6'
  },
  'Alerts': {
    name: 'Alerts',
    description: 'Time-sensitive notifications and warnings',
    icon: 'pi-bell',
    color: '#EF4444'
  },
  'Performance': {
    name: 'Performance',
    description: 'Team and individual performance metrics',
    icon: 'pi-trophy',
    color: '#F59E0B'
  },
  'Sales': {
    name: 'Sales',
    description: 'Sales pipeline and revenue analysis',
    icon: 'pi-chart-line',
    color: '#10B981'
  },
  'Activity': {
    name: 'Activity',
    description: 'User and system activity tracking',
    icon: 'pi-users',
    color: '#8B5CF6'
  },
  'Personal': {
    name: 'Personal',
    description: 'Individual user reports and metrics',
    icon: 'pi-user',
    color: '#06B6D4'
  },
  'Analytics': {
    name: 'Analytics',
    description: 'Comprehensive business intelligence',
    icon: 'pi-chart-pie',
    color: '#84CC16'
  }
};

// Helper function to get current quarter dates
export function getCurrentQuarterDates(): { start: Date; end: Date } {
  const now = new Date();
  const currentQuarter = Math.floor(now.getMonth() / 3);
  const quarterStartMonth = currentQuarter * 3;
  
  return {
    start: new Date(now.getFullYear(), quarterStartMonth, 1),
    end: new Date(now.getFullYear(), quarterStartMonth + 3, 0)
  };
}

// Helper function to get last completed quarter dates
export function getLastCompletedQuarterDates(): { start: Date; end: Date } {
  const now = new Date();
  const currentQuarter = Math.floor(now.getMonth() / 3);
  const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
  const year = currentQuarter === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const quarterStartMonth = lastQuarter * 3;
  
  return {
    start: new Date(year, quarterStartMonth, 1),
    end: new Date(year, quarterStartMonth + 3, 0)
  };
}

console.log('📊 Report Templates Loaded: 14 templates ready for custom reports system (11 business templates + 3 comprehensive demos)');
